import {PayrollCompensationItemType} from "../../__generated__/generated_types";
import {getEmployeePropertyLocked} from "../../components/analyst-payroll-drawer/helpers/utils";
import {ensureDateInLocale} from "../../utils/helper-funcs";
import {TLockedProperties} from "../bulk-update/BulkUpdate";
import {PayrollQueryData} from "./PayrollDataLoader";
import {PayrollEmployeeDataRow, TaxFilter, TaxFilterElementType, TaxFilterOptions} from "./types";


export function buildPayrollDataRows(dbData: PayrollQueryData, propertyLocks: TLockedProperties): PayrollEmployeeDataRow[] {
    const rows: PayrollEmployeeDataRow[] = [];
    let empNum = 0;
    for (const emp of dbData.sortBy(row => (row.firstName ?? "") + row.lastName)) {
        empNum++;
        const medical = emp.compItems.filter(ci => ci.compensationItem.type == 'MEDICAL');
        const dental = emp.compItems.filter(ci => ci.compensationItem.type == 'DENTAL');
        const vision = emp.compItems.filter(ci => ci.compensationItem.type == 'VISION');
        const r401k = emp.compItems.filter(ci => ci.compensationItem.type == 'RETIREMENT_401K_MATCH');
        const medicare = emp.compItems.filter(ci => ci.compensationItem.type == 'MEDICARE_TAX');
        const socialSecurity = emp.compItems.filter(ci => ci.compensationItem.type == 'SOCIAL_SECURITY_TAX');
        const futa = emp.compItems.filter(ci => ci.compensationItem.type == 'FEDERAL_UNEMPLOYMENT_TAX');
        const suta = emp.compItems.filter(ci => ci.compensationItem.type == 'STATE_UNEMPLOYMENT_TAX');
        const wc = emp.compItems.filter(ci => ci.compensationItem.type == 'WORKERS_COMPENSATION');
        const maxRows = Math.max(
            1,
            emp.raises.length,
            emp.bonuses.length,
            emp.overtime.length,
            emp.propertyAttributions.length,
            medical.length,
            dental.length,
            vision.length,
            r401k.length,
            medicare.length,
            socialSecurity.length,
            futa.length,
            suta.length,
            wc.length,
        );

        const propertyLocked = getEmployeePropertyLocked(emp.propertyAttributions.map(pa => pa.property.id), propertyLocks);

        for (let i = 0; i < maxRows; i++) {

            let attribution: PayrollEmployeeDataRow["propertyAttribution"] = undefined;
            if(emp.propertyAttributions.length > i) {
                // TS Compiler is very pessimistic here about things being undefined that can't be.
                const propAttribution = emp.propertyAttributions[i];
                if(propAttribution) {
                    attribution = {
                        property: {
                            id: propAttribution.property.id,
                            name: propAttribution.property.displayName ?? propAttribution.property.name
                        },
                        attributionPercentage: propAttribution.attributionPercentage
                    };
                }
            }

            rows.push({
                empNum: i == 0 ? empNum : undefined,
                empRowNum: i,
                employeeId: emp.id,
                employee: i == 0 ? emp : undefined,
                position: i == 0 ? emp.position : undefined,
                propertyAttribution: attribution,
                baseComp: i == 0 ? emp.baseComp : undefined,
                overtime: emp.overtime.length > i ? emp.overtime[i] : undefined,
                bonus: emp.bonuses.length > i ? emp.bonuses[i] : undefined,
                raise: emp.raises.length > i ? emp.raises[i] : undefined,
                medical: medical.length > i ? medical[i] : undefined,
                dental: dental.length > i ? dental[i] : undefined,
                vision: vision.length > i ? vision[i] : undefined,
                r401k: r401k.length > i ? r401k[0] : undefined,
                medicare: medicare.length > i ? medicare[i] : undefined,
                socialSecurity: socialSecurity.length > i ? socialSecurity[i] : undefined,
                futa: futa.length > i ? futa[i] : undefined,
                suta: suta.length > i ? suta[i] : undefined,
                wc: wc.length > i ? wc[i] : undefined,
                propertyLocked: propertyLocked,
            });
        }
    }
    return rows;
}

export function filterPayrollEmployees({
    dbData,
    propertyId,
    nameSearchString,
    filterPropertyIds,
    filterPositions,
    raiseOnceDateFilter,
    raiseRecurrDateFilter,
    bonusOnceDateFilter,
    bonusRecurrDateFilter,
    overtimeDateFilter,
    taxFilter: taxFilter
}: {
    dbData: PayrollQueryData,
    propertyId: string | undefined,
    nameSearchString: string | undefined,
    filterPropertyIds: string[] | undefined,
    filterPositions: string[],
    raiseOnceDateFilter: Date | undefined,
    raiseRecurrDateFilter: {raiseMonth: number, raiseDay: number} | undefined,
    bonusOnceDateFilter: Date | undefined,
    bonusRecurrDateFilter: {bonusMonth: number, bonusDay: number} | undefined,
    overtimeDateFilter: Date | undefined,
    taxFilter: TaxFilter
}
): PayrollQueryData {
    const itemTypesToFilterByValue: Map<PayrollCompensationItemType, string> = new Map();
    const itemTypesToNotFilter: PayrollCompensationItemType[] = [];
    const itemTypesToFilterEmpty: PayrollCompensationItemType[] = [];

    if (Object.values(taxFilter).some(v => v !== undefined)) {
        for (const [key, value] of Object.entries(taxFilter)) {
            let pcit = undefined;
            switch (key) {
                case "r401k":
                    pcit = PayrollCompensationItemType.Retirement_401KMatch;
                    break;
                case "medicare":
                    pcit = PayrollCompensationItemType.MedicareTax;
                    break;
                case "socialSecurity":
                    pcit =  PayrollCompensationItemType.SocialSecurityTax;
                    break;
                case "futa":
                    pcit = PayrollCompensationItemType.FederalUnemploymentTax;
                    break;
                case "suta":
                    pcit =  PayrollCompensationItemType.StateUnemploymentTax;
                    break;
                case "wc":
                    pcit = PayrollCompensationItemType.WorkersCompensation;
                    break;
            }
            if (!pcit) {
                continue;
            }
            if (value === "empty") {
                itemTypesToFilterEmpty.push(pcit);
            }
            else if (value === undefined) {
                itemTypesToNotFilter.push(pcit);
            }
            else {
                itemTypesToFilterByValue.set(pcit, value);
            }
        }
    }
    const itemTypesToFilterByValueAsArray = Array.from(itemTypesToFilterByValue);

    function compItemFitsTheFilter(compItem: PayrollQueryData[0]["compItems"][0]): boolean {
        const valueFilter = itemTypesToFilterByValue.get(compItem.compensationItem.type);
        const ret =
              (valueFilter === undefined || Number(valueFilter) == Number(compItem.costPerPayPeriod))
              && !itemTypesToFilterEmpty.includes(compItem.compensationItem.type)
              || itemTypesToNotFilter.includes(compItem.compensationItem.type);
        return ret;
    }

    function allFilterValuesFoundForCompItems(compItems: PayrollQueryData[0]["compItems"]): boolean {
        for (const [pcit, value] of itemTypesToFilterByValueAsArray) {
            if (!compItems.find(compItem => compItem.compensationItem.type == pcit && Number(value) == Number(compItem.costPerPayPeriod))) {
                return false;
            }
        }
        return true;
    }

    // Ideally use dedicated propertyId aware endpoint - just lazy to do it.
    let filteredDbData = dbData;
    if (propertyId) {
        filteredDbData = filteredDbData.filter(row => row.propertyAttributions.map(p => p.property.id).includes(propertyId));
    }
    else if (filterPropertyIds) {
        filteredDbData = filteredDbData.filter(row => row.propertyAttributions.find(p => filterPropertyIds.includes(p.property.id)));
    }

    if (nameSearchString && nameSearchString.length > 0) {
        const nameSearchStringLowered = nameSearchString.toLowerCase();
        filteredDbData = filteredDbData.filter(row => {
            const employeeName = row.firstName + (row.lastName ? " " + row.lastName : "");
            return employeeName.toLowerCase().includes(nameSearchStringLowered);
        });
    }

    filteredDbData = filteredDbData.filter(row => filterPositions.includes(row.position.name));

    if (raiseOnceDateFilter) {
        const dateFilteredDbData: PayrollQueryData = [];
        for (const emp of filteredDbData) {
            const filteredEmpRaises = emp.raises.filter(raise =>
                raise.frequency == "ONCE"
                && raise.onetimeRaiseDate
                && (ensureDateInLocale(raise.onetimeRaiseDate)?.getTime() == raiseOnceDateFilter.getTime()));
            if (filteredEmpRaises.length > 0) {
                dateFilteredDbData.push({
                    ...emp,
                    raises: filteredEmpRaises
                });
            }
        }
        filteredDbData = dateFilteredDbData;
    }
    else if (raiseRecurrDateFilter) {
        const dateFilteredDbData: PayrollQueryData = [];
        for (const emp of filteredDbData) {
            const filteredEmpRaises = emp.raises.filter(raise =>
                raise.frequency == "ANNUALLY"
                && raise.raiseMonth == raiseRecurrDateFilter.raiseMonth
                && raise.raiseDay == raiseRecurrDateFilter.raiseDay
            );
            if (filteredEmpRaises.length > 0) {
                dateFilteredDbData.push({
                    ...emp,
                    raises: filteredEmpRaises
                });
            }
        }
        filteredDbData = dateFilteredDbData;
    }

    if (bonusOnceDateFilter) {
        const dateFilteredDbData: PayrollQueryData = [];
        for (const emp of filteredDbData) {
            const filteredEmpBonuses = emp.bonuses.filter(bonus =>
                bonus.frequency == "ONCE"
                && bonus.onetimeBonusDate
                && (ensureDateInLocale(bonus.onetimeBonusDate)?.getTime() == bonusOnceDateFilter.getTime()));
            if (filteredEmpBonuses.length > 0) {
                dateFilteredDbData.push({
                    ...emp,
                    bonuses: filteredEmpBonuses
                });
            }
        }
        filteredDbData = dateFilteredDbData;
    }
    else if (bonusRecurrDateFilter) {
        const dateFilteredDbData: PayrollQueryData = [];
        for (const emp of filteredDbData) {
            const filteredEmpBonuses = emp.bonuses.filter(bonus =>
                bonus.frequency == "ANNUALLY"
                && bonus.bonusMonth == bonusRecurrDateFilter.bonusMonth
                && bonus.bonusDay == bonusRecurrDateFilter.bonusDay
            );
            if (filteredEmpBonuses.length > 0) {
                dateFilteredDbData.push({
                    ...emp,
                    bonuses: filteredEmpBonuses
                });
            }
        }
        filteredDbData = dateFilteredDbData;
    }

    if (overtimeDateFilter) {
        const dateFilteredDbData: PayrollQueryData = [];
        for (const emp of filteredDbData) {
            const filteredEmpOvertimes = emp.overtime.filter(overtime =>
                overtime.effectiveStartDate
                && (ensureDateInLocale(overtime.effectiveStartDate)?.getTime() == overtimeDateFilter.getTime()));
            if (filteredEmpOvertimes.length > 0) {
                dateFilteredDbData.push({
                    ...emp,
                    overtime: filteredEmpOvertimes
                });
            }
        }
        filteredDbData = dateFilteredDbData;
    }

    if (itemTypesToFilterEmpty.length > 0 || itemTypesToFilterByValue.size > 0) {
        const filtered: PayrollQueryData = [];
        for (const emp of filteredDbData) {
            if (emp.compItems.every(compItemFitsTheFilter)
                && allFilterValuesFoundForCompItems(emp.compItems)) {
                filtered.push(emp);
            }
        }

        filteredDbData = filtered;
    }

    return filteredDbData;
}


export function countPayrollEmployees(rows: PayrollEmployeeDataRow[]): number {
    return rows.dedupe("employeeId").length;
}

export function countUnlockedPayrollEmployees(rows: PayrollEmployeeDataRow[]): number {
    return countPayrollEmployees(rows.filter(e => !e.propertyLocked));
}

export function getPositions(data: PayrollQueryData): string[] {
    return data.map(emp => emp.position.name).dedupe().sort();
}

export function buildTaxFilterOptions(payrollEmployeeData: PayrollQueryData | undefined): TaxFilterOptions {
    const ret: TaxFilterOptions = {
        r401k: [{value: "any", label: "Any"}, {value: "empty", label: "NOT SET"}],
        medicare: [{value: "any", label: "Any"}, {value: "empty", label: "NOT SET"}],
        socialSecurity: [{value: "any", label: "Any"}, {value: "empty", label: "NOT SET"}],
        futa: [{value: "any", label: "Any"}, {value: "empty", label: "NOT SET"}],
        suta: [{value: "any", label: "Any"}, {value: "empty", label: "NOT SET"}],
        wc: [{value: "any", label: "Any"}, {value: "empty", label: "NOT SET"}]
    };

    function toOptionsArray(values: Set<string>): {value: string, label: string}[] {
        const asArray = Array.from(values).map(v => Number(v));
        asArray.sort((a, b) => a - b);
        return asArray.map(v => ({
            value: String(v),
            label: String(v)
        }));
    }

    if (payrollEmployeeData) {
        const collectedOptions = new Map<PayrollCompensationItemType, Set<string>>();
        for (const emp of payrollEmployeeData) {
            for (const compItem of emp.compItems) {
                let compTypeValues = collectedOptions.get(compItem.compensationItem.type);
                if (!compTypeValues) {
                    compTypeValues = new Set();
                    collectedOptions.set(compItem.compensationItem.type, compTypeValues);
                }
                compTypeValues.add(compItem.costPerPayPeriod);
            }
        }

        ret.r401k = ret.r401k.concat(toOptionsArray(collectedOptions.get(PayrollCompensationItemType.Retirement_401KMatch) ?? new Set()));
        ret.medicare = ret.medicare.concat(toOptionsArray(collectedOptions.get(PayrollCompensationItemType.MedicareTax) ?? new Set()));
        ret.socialSecurity = ret.socialSecurity.concat(toOptionsArray(collectedOptions.get(PayrollCompensationItemType.SocialSecurityTax) ?? new Set()));
        ret.futa = ret.futa.concat(toOptionsArray(collectedOptions.get(PayrollCompensationItemType.FederalUnemploymentTax) ?? new Set()));
        ret.suta = ret.suta.concat(toOptionsArray(collectedOptions.get(PayrollCompensationItemType.StateUnemploymentTax) ?? new Set()));
        ret.wc = ret.wc.concat(toOptionsArray(collectedOptions.get(PayrollCompensationItemType.WorkersCompensation) ?? new Set()));
    }

    return ret;
}

export const DEFAULT_TAX_FILTER_OPTIONS = buildTaxFilterOptions(undefined);

export function updateTaxFilter(prev: TaxFilter, what: keyof TaxFilter, value: TaxFilterElementType): TaxFilter {
    return {
        ...prev,
        [what]: value
    };
}